'use client';

import { createContext, useContext, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import {
  type PublicLocale,
  getLocaleFromPathname,
  hasLocalePrefix,
  isPublicLocale,
  localizeHref,
  switchLocalePathname,
} from '@/lib/locale';

type LocaleContextValue = {
  locale: PublicLocale;
  localizePath: (href: string) => string;
  switchLocalePath: (targetLocale: PublicLocale) => string;
};

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  localizePath: (href) => href,
  switchLocalePath: () => '/en',
});

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const locale = useMemo<PublicLocale>(() => {
    if (hasLocalePrefix(pathname)) {
      return getLocaleFromPathname(pathname);
    }

    if (typeof document !== 'undefined') {
      const cookieLocale = document.cookie
        .split(';')
        .map((chunk) => chunk.trim())
        .find((chunk) => chunk.startsWith('blessing_locale='))
        ?.split('=')[1];
      if (cookieLocale && isPublicLocale(cookieLocale)) {
        return cookieLocale;
      }
    }

    return getLocaleFromPathname(pathname);
  }, [pathname]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.cookie = `blessing_locale=${locale}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    }
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      localizePath: (href) => localizeHref(locale, href),
      switchLocalePath: (targetLocale) => switchLocalePathname(pathname, targetLocale),
    }),
    [locale, pathname]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): PublicLocale {
  return useContext(LocaleContext).locale;
}

export function useLocalizedPath(): (href: string) => string {
  return useContext(LocaleContext).localizePath;
}

export function useSwitchLocalePath(): (targetLocale: PublicLocale) => string {
  return useContext(LocaleContext).switchLocalePath;
}
